import { IAllIssueColumnStates, ISelectedGisMap } from "@mctypes/frontend.types";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { IAllFilter } from "@schemas/commonSchema";
import { IIssueViewEnum } from "@schemas/issueBoardSchema";
import { ISortColumn, IStoredIssueColConfigs } from "@schemas/issueSchema";
import { ICapitalLetter, IOtherAttributesMathFormula } from "@schemas/otherAttributesSchema";
import * as R from "ramda";
import { addAppModalThunk } from "../../../global/globalSlice";
import { IssueViewEnum } from "../../../mc-shared/enums";
import { AppThunk } from "../../../store";
import {
  ISSUE_COLUMN_LS_KEY,
  ISSUE_DATAFILEDS_ENUM,
  ISSUE_USE_MANTINE_MODAL_LS_KEY,
  ISSUE_VIEW_LS_KEY,
  IssueKanbanGroupingViewEnum,
} from "./issueConstants";
import { selectGetOpenIssueArgsModalSelector } from "./issueSelectors";

export type IssueDataFieldsObject = {
  [key in keyof typeof ISSUE_DATAFILEDS_ENUM]: boolean;
};

export type IIssueDropdownFilters = {
  [key in ISSUE_DATAFILEDS_ENUM]?: string[];
};

export interface IFloorplanState {
  selectedFloor: string;
  selectedFloorSpaceId: string;
  roomSearchText: string;
  floorSearchText: string;
  showOnlyFloorsWithIssues: boolean;
  floorplanRotation: number;
}

export interface IssueReducer {
  allFiltersIssueBoardId: string | null;
  selectedPnsFilterItem: string; // 1.1.1
  selectedPnsBoardFilterItem: string; // 1.1.1
  floorplan: IFloorplanState;
  activeTableTemplateId: string | null;
  activeView: IIssueViewEnum;
  kanbanGroupingView: IssueKanbanGroupingViewEnum;
  selectedGisMap: ISelectedGisMap;
  allColumnState: IAllIssueColumnStates;
  sortedCol: ISortColumn;
  activeDashboard: string;
  currentIssueBoardId: string | null; // Added to keep track of current issue board ID
  useMantineModal: boolean;
  svarGant: {
    zoomLevel: "week" | "month";
    startDate: string | null;
    endDate: string | null;
    skipItemsWithoutEndDate: boolean;
  };
  dropdownFilters: IIssueDropdownFilters;
  miniSearchFreeText: string;
  taskAndOrIssueArgs: ("TASK" | "ISSUE")[];
  issueLogsCreatedAtRange: [string | null, string | null];
  issueLogsPageSize: number;
  issueLogsCurrentPage: number;
  mathFormulaConfigDraft: IOtherAttributesMathFormula | null;
  formulaCounterGroupIssuesModal: {
    issueId: string;
    formulaAttributeKey: ICapitalLetter;
  } | null;
}

// Helper functions to get initial dates
const getInitStartDate = () => {
  const today = new Date();
  return new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split("T")[0]; // Beginning of current month
};

const getInitEndDate = () => {
  const today = new Date();
  return new Date(today.getFullYear(), today.getMonth() + 2, 0).toISOString().split("T")[0]; // End of next month
};

const filtersToInclude = [ISSUE_DATAFILEDS_ENUM.TAGS, ISSUE_DATAFILEDS_ENUM.PRIORITY, ISSUE_DATAFILEDS_ENUM.STATUS];

const emptyFilters: Record<ISSUE_DATAFILEDS_ENUM, string[]> = Object.values(filtersToInclude).reduce((acc, key) => {
  acc[key] = [];
  return acc;
}, {} as Record<ISSUE_DATAFILEDS_ENUM, string[]>);

const getInitShowOnlyFloorsWithIssues = () => {
  return localStorage.getItem("showOnlyFloorsWithIssues") === "true";
};

const getInitFloorplanRotation = () => {
  const stored = localStorage.getItem("floorplanRotation");
  const parsed = stored != null ? Number(stored) : NaN;
  return Number.isFinite(parsed) ? parsed : 270;
};

const getInitialFloorplanState = (): IFloorplanState => ({
  selectedFloor: "",
  selectedFloorSpaceId: "",
  roomSearchText: "",
  floorSearchText: "",
  showOnlyFloorsWithIssues: getInitShowOnlyFloorsWithIssues(),
  floorplanRotation: getInitFloorplanRotation(),
});

const initialState: IssueReducer = {
  allFiltersIssueBoardId: null,
  selectedPnsFilterItem: null,
  selectedPnsBoardFilterItem: null,
  floorplan: getInitialFloorplanState(),
  activeTableTemplateId: null,
  activeView: (localStorage.getItem(ISSUE_VIEW_LS_KEY) as IssueViewEnum) || IssueViewEnum.TABLE,
  kanbanGroupingView: IssueKanbanGroupingViewEnum.STATUS,
  selectedGisMap: null,
  allColumnState: JSON.parse(localStorage.getItem(ISSUE_COLUMN_LS_KEY) || "{}"),
  sortedCol: null,
  activeDashboard: null,
  currentIssueBoardId: null,
  useMantineModal: localStorage.getItem(ISSUE_USE_MANTINE_MODAL_LS_KEY) !== "false",
  svarGant: {
    zoomLevel: "week",
    startDate: localStorage.getItem("svarGantStartDate") || getInitStartDate(),
    endDate: localStorage.getItem("svarGantEndDate") || getInitEndDate(),
    skipItemsWithoutEndDate: false,
  },
  dropdownFilters: {} as Record<ISSUE_DATAFILEDS_ENUM, string[]>,
  miniSearchFreeText: "",
  taskAndOrIssueArgs: ["ISSUE"],
  issueLogsCreatedAtRange: [null, null],
  issueLogsPageSize: 500,
  issueLogsCurrentPage: 1,
  mathFormulaConfigDraft: null,
  formulaCounterGroupIssuesModal: null,
};

export const setIssueAllFiltersIfNoActiveTemplateThunk =
  (filters: IAllFilter[]): AppThunk =>
  (dispatch: any, getState) => {
    const { activeTableTemplateId } = getState().issueReducerNew;
    if (activeTableTemplateId == null) {
      // dispatch(setIssueAllFiltersInRedux(filters));
    }
  };

export const updateColumnStateThunk = (columnState: IStoredIssueColConfigs[]): AppThunk => {
  return (dispatch, getState) => {
    const allColumnStateCopy = R.clone(getState().issueReducerNew.allColumnState);
    const issueBoardId = getState().issueReducerNew.currentIssueBoardId;
    const updatedData = {
      ...allColumnStateCopy,
      [issueBoardId]: columnState,
    };
    if (columnState === null) {
      delete updatedData[issueBoardId];
    }
    localStorage.setItem(ISSUE_COLUMN_LS_KEY, JSON.stringify(updatedData));
    dispatch(setIssueColumnStateInRedux(updatedData));
  };
};
export const issueSlice = createSlice({
  name: "issue",
  initialState,
  reducers: {
    setIssueActiveTableTemplateIdInRedux: (state, action: PayloadAction<string | null>) => {
      state.activeTableTemplateId = action.payload;
    },
    setIssueActiveViewInRedux: (state, action: PayloadAction<IIssueViewEnum>) => {
      localStorage.setItem(ISSUE_VIEW_LS_KEY, action.payload);
      state.activeView = action.payload;
    },
    setKanbanGroupingViewInRedux: (state, action: PayloadAction<IssueKanbanGroupingViewEnum>) => {
      state.kanbanGroupingView = action.payload;
    },
    resetIssueRedux: (state) => {
      state.activeTableTemplateId = null;
      state.kanbanGroupingView = IssueKanbanGroupingViewEnum.STATUS;
    },
    setIssueSelectedGisMapInRedux: (state, action: PayloadAction<ISelectedGisMap>) => {
      state.selectedGisMap = action.payload;
    },
    setIssueColumnStateInRedux: (state, action: PayloadAction<IAllIssueColumnStates>) => {
      state.allColumnState = action.payload;
    },
    setSelectedPnsFilterItemInRedux: (state, action: PayloadAction<string>) => {
      state.selectedPnsFilterItem = action.payload;
    },
    setIssueSortedColInRedux: (state, action: PayloadAction<ISortColumn>) => {
      state.sortedCol = action.payload;
    },
    setActiveDashboardInRedux: (state, action: PayloadAction<string>) => {
      state.activeDashboard = action.payload;
    },
    setIssueSelectedPnsBoardFilterItemInRedux: (state, action: PayloadAction<string>) => {
      state.selectedPnsBoardFilterItem = action.payload;
    },
    setSelectedFloorInRedux: (state, action: PayloadAction<string>) => {
      state.floorplan.selectedFloor = action.payload;
      state.floorplan.selectedFloorSpaceId = "";
    },
    setSelectedFloorSpaceIdInRedux: (state, action: PayloadAction<string>) => {
      state.floorplan.selectedFloorSpaceId = action.payload;
    },
    setRoomSearchTextInRedux: (state, action: PayloadAction<string>) => {
      state.floorplan.roomSearchText = action.payload;
    },
    setFloorSearchTextInRedux: (state, action: PayloadAction<string>) => {
      state.floorplan.floorSearchText = action.payload;
    },
    setShowOnlyFloorsWithIssuesInRedux: (state, action: PayloadAction<boolean>) => {
      state.floorplan.showOnlyFloorsWithIssues = action.payload;
      localStorage.setItem("showOnlyFloorsWithIssues", String(action.payload));
    },
    setFloorplanRotationInRedux: (state, action: PayloadAction<number>) => {
      state.floorplan.floorplanRotation = action.payload;
      localStorage.setItem("floorplanRotation", String(action.payload));
    },
    setCurrentIssueboardId: (state, action: PayloadAction<string | null>) => {
      state.currentIssueBoardId = action.payload;
    },
    setAllFiltersIssueBoardId: (state, action: PayloadAction<string | null>) => {
      state.allFiltersIssueBoardId = action.payload;
    },
    setUseMantineModalInRedux: (state, action: PayloadAction<boolean>) => {
      state.useMantineModal = action.payload;
      localStorage.setItem(ISSUE_USE_MANTINE_MODAL_LS_KEY, String(action.payload));
    },
    setSvarGantZoomLevelInRedux: (state, action: PayloadAction<"week" | "month">) => {
      state.svarGant.zoomLevel = action.payload;
    },
    setSvarGantStartDateInRedux: (state, action: PayloadAction<string | null>) => {
      const newStartDate = action.payload;
      state.svarGant.startDate = newStartDate;
      localStorage.setItem("svarGantStartDate", newStartDate || "");
      if (!newStartDate) {
        state.svarGant.startDate = localStorage.getItem("svarGantStartDate") || getInitStartDate(); // Default to beginning of current month
      }
    },
    setSvarGantEndDateInRedux: (state, action: PayloadAction<string | null>) => {
      const newEndDate = action.payload;
      state.svarGant.endDate = newEndDate;
      localStorage.setItem("svarGantEndDate", newEndDate || "");
      if (!newEndDate) {
        state.svarGant.endDate = localStorage.getItem("svarGantEndDate") || getInitEndDate(); // Default to end of next month
      }
    },
    setSvarGantSkipItemsWithoutEndDateInRedux: (state, action: PayloadAction<boolean>) => {
      state.svarGant.skipItemsWithoutEndDate = action.payload;
    },
    setIssueDropdownSingleDataFieldFilterInRedux: (
      state,
      action: PayloadAction<{ filter: ISSUE_DATAFILEDS_ENUM; values: string[] }>,
    ) => {
      const { filter, values } = action.payload;
      state.dropdownFilters[filter] = values;
    },
    setIssueDropdownFilterAllInRedux: (state, action: PayloadAction<{ filters: IIssueDropdownFilters }>) => {
      state.dropdownFilters = action.payload.filters;
    },
    clearAllDropdownFilterInRedux: (state) => {
      state.dropdownFilters = {};
    },
    setMiniSearchFreeTextInRedux: (state, action: PayloadAction<string>) => {
      state.miniSearchFreeText = action.payload;
    },
    setTaskAndOrIssueArgsInRedux: (state, action: PayloadAction<("TASK" | "ISSUE")[]>) => {
      state.taskAndOrIssueArgs = action.payload;
    },
    //Historik-tabben
    setIssueLogsCreatedAtRangeInRedux: (state, action: PayloadAction<[string | null, string | null]>) => {
      state.issueLogsCreatedAtRange = action.payload;
    },
    setIssueLogsPageSizeInRedux: (state, action: PayloadAction<number>) => {
      state.issueLogsPageSize = action.payload;
    },
    setIssueLogsCurrentPageInRedux: (state, action: PayloadAction<number>) => {
      state.issueLogsCurrentPage = action.payload;
    },
    setMathFormulaConfigDraftInRedux: (state, action: PayloadAction<IOtherAttributesMathFormula | null>) => {
      state.mathFormulaConfigDraft = action.payload;
    },
    resetMathFormulaConfigInRedux: (state) => {
      state.mathFormulaConfigDraft = null;
    },
    setFormulaCounterGroupIssuesModalInRedux: (
      state,
      action: PayloadAction<{ issueId: string; formulaAttributeKey: ICapitalLetter } | null>,
    ) => {
      state.formulaCounterGroupIssuesModal = action.payload;
    },
  },
});

export const thunkOpenIssueModalById =
  (issueId: string): AppThunk =>
  async (dispatch, getState) => {
    const state = getState();
    const args = selectGetOpenIssueArgsModalSelector(state, issueId);
    if (args.projectId == null || args.boardId == null) {
      console.warn("Kan ikke åpne issue modal – mangler projectId eller boardId");
      return;
    }
    dispatch(addAppModalThunk(args));
  };

export const {
  setIssueActiveTableTemplateIdInRedux,
  setIssueActiveViewInRedux,
  setKanbanGroupingViewInRedux,
  resetIssueRedux,
  setIssueSelectedGisMapInRedux,
  setSelectedPnsFilterItemInRedux,
  setIssueSortedColInRedux,
  setActiveDashboardInRedux,
  setIssueColumnStateInRedux,
  setIssueSelectedPnsBoardFilterItemInRedux,
  setSelectedFloorInRedux,
  setSelectedFloorSpaceIdInRedux,
  setRoomSearchTextInRedux,
  setFloorSearchTextInRedux,
  setShowOnlyFloorsWithIssuesInRedux,
  setFloorplanRotationInRedux,
  setCurrentIssueboardId,
  setAllFiltersIssueBoardId,
  setUseMantineModalInRedux,
  setSvarGantZoomLevelInRedux,
  setSvarGantStartDateInRedux,
  setSvarGantEndDateInRedux,
  setSvarGantSkipItemsWithoutEndDateInRedux,
  setIssueDropdownSingleDataFieldFilterInRedux,
  setMiniSearchFreeTextInRedux,
  setTaskAndOrIssueArgsInRedux,
  clearAllDropdownFilterInRedux,
  setIssueDropdownFilterAllInRedux,
  setIssueLogsCreatedAtRangeInRedux,
  setIssueLogsPageSizeInRedux,
  setIssueLogsCurrentPageInRedux,
  setMathFormulaConfigDraftInRedux,
  resetMathFormulaConfigInRedux,
  setFormulaCounterGroupIssuesModalInRedux,
} = issueSlice.actions;

export default issueSlice.reducer;
